import { defaultTo, flatten, head, is, pipe, split } from 'ramda';
import { uniq, get } from 'lodash';

export function getHomeworkDraftTaxonomy(exercises = []) {
    if (exercises.length === 0) {
        return '';
    }

    const exercisesTaxonomy = flatten(exercises.map(exercise => exercise.taxonomy)).filter(a => a);
    const { topics, themes, microthemes } = exercisesTaxonomy.reduce(
        (acc, item) => {
            return {
                topics: uniq(acc.topics.concat(get(item, 'topic.name'))).filter(a => a),
                themes: uniq(acc.themes.concat(get(item, 'theme.name'))).filter(a => a),
                microthemes: uniq(acc.microthemes.concat(get(item, 'microtheme.name'))).filter(a => a)
            };
        },
        { topics: [], themes: [], microthemes: [] }
    );
    if (topics.length > 1) {
        return topics.join(' / ');
    }
    if (themes.length > 1 || themes.length === 0) {
        return topics[0] || '';
    }
    if (microthemes.length > 1 || microthemes.length === 0) {
        return `${topics[0]}: ${themes[0]}`;
    }
    return `${topics[0]}: ${themes[0]} / ${microthemes[0]}`;
}

export function convertContentPathsToTaxonomy(contentPaths = []) {
    /*
     *   Example input:
     *
     *       // Level 0 paths
     *       [{
     *           context: 2000015711,
     *           // Level 1 paths
     *           paths: [{
     *               verb: 1,
     *               // Level 2 paths
     *               paths: [{
     *                   id: 2000015711,
     *                   name: 'Inglese',
     *                   child: {
     *                       id: 2000017919,
     *                       name: 'Lessico',
     *                       child: {
     *                           id: 2000017958,
     *                           name: 'Il tempo meteorologico',
     *                           child: {
     *                               id: 2000017959,
     *                               name: 'Descrivere le condizioni meteorologiche'
     *                           }
     *                       }
     *                   }
     *               }]
     *           }]
     *       }]
     *
     *   Example output:
     *
     *       [{
     *           subject: {name: 'Inglese'},
     *           topic: {name: 'Lessico'},
     *           theme: {name: 'Il tempo meteorologico'},
     *           microtheme: {name: 'Descrivere le condizioni meteorologiche'}
     *       }]
     *
     */
    return (
        contentPaths
            // Get a list of all 'level 1' paths
            .reduce((paths, path) => paths.concat(path.paths), [])
            // Only keep 'level 1' paths with verb === 1
            .filter(path => path.verb === 1)
            // Get a list of all 'level 2' paths
            .reduce((paths, path) => paths.concat(path.paths), [])
            // Get a list of [topic, microtheme] pairs
            .map(path => {
                const topic = get(path, 'child', {});
                const theme = get(path, 'child.child', {});
                const microtheme = get(path, 'child.child.child', {});
                return {
                    id: `${path.id}${topic.id ? `.${topic.id}` : ''}${theme.id ? `.${theme.id}` : ''}${microtheme.id ? `.${microtheme.id}` : ''}`,
                    subject: { name: path.name },
                    ...(topic.name && { topic: { name: topic.name } }),
                    ...(theme.name && { theme: { name: theme.name } }),
                    ...(microtheme.name && { microtheme: { name: microtheme.name } })
                };
            })
    );
}

export function exerciseTaxonomyNames(taxonomy) {
    let out = [];

    if (!taxonomy || !Array.isArray(taxonomy)) {
        return out;
    }

    let element;
    let topic;
    let theme;
    let microtheme;

    for (let i = 0; i < taxonomy.length; i++) {
        element = '';
        topic = get(taxonomy[i], 'topic');
        theme = get(taxonomy[i], 'theme');
        microtheme = get(taxonomy[i], 'microtheme');

        if (topic && topic.name && topic.name !== '') {
            element = topic.name;
            if (theme && theme.name && theme.name !== '') {
                element = element.concat(`: ${theme.name}`);
                if (microtheme && microtheme.name && microtheme.name !== '') {
                    element = element.concat(` / ${microtheme.name}`);
                }
            }
        }

        if (element !== '') {
            out.push(element);
        }
    }

    return out;
}

export function getTaxonomyIdAndName(taxonomy) {
    if (!is(Object, taxonomy)) {
        return null;
    }
    return {
        ...taxonomy,
        value: get(taxonomy, 'microtheme.name') || get(taxonomy, 'theme.name') || get(taxonomy, 'topic.name', '')
    };
}

export const getContextId = pipe(
    defaultTo([]),
    head,
    element => (is(String, element) ? element : `${element}`),
    split('.'),
    head,
    parseFloat
);
